import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAnimation } from '../contexts/AnimationContext';
import { isAuthenticated, clearTokens } from '../services/api';

const TopBar = () => {
  const { t } = useTranslation('nav');
  const { t: tCommon } = useTranslation('common');
  const navigate = useNavigate();
  const location = useLocation();
  const { 
    useTransformersAnimation, 
    toggleAnimationMode,
    useRouteAnimatePresence,
    toggleRouteAnimatePresence
  } = useAnimation();

  // Check if user is authenticated
  const authenticated = isAuthenticated();
  
  // Don't show logout on auth pages
  const isAuthPage = ['/login', '/register', '/forgot-password', '/reset-password'].includes(location.pathname);
  const showLogout = authenticated && !isAuthPage;

  const handleLogout = () => {
    clearTokens();
    navigate('/login', { replace: true });
  };

  return (
    <nav className="absolute flex items-center justify-between left-0 md:px-12 mx-auto p-4 md:p-6 top-0 w-full z-20" style={{display:"none"}}>
      <div className="flex items-center gap-2 md:gap-3 cursor-pointer group">
        <div className="size-8 md:size-10 bg-nokia-blue rounded-lg flex items-center justify-center transition-all duration-300 shadow-[0_4px_12px_rgba(0,69,245,0.2)]">
          <svg className="text-white w-4 h-4 md:w-6 md:h-6" fill="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path d="M4 4h4v16H4V4zm6 0h4v16h-4V4zm6 0h4v16h-4V4z"></path>
          </svg>
        </div>
        <h2 className="text-nokia-blue text-base md:text-xl font-bold tracking-tight">
          NOKIA<span className="font-light opacity-80 text-gray-600">NET</span>
        </h2>
      </div>
      <div className="flex items-center gap-2 md:gap-4">
        {/* Main Context Toggle Checkbox - switches between Route AP and Simple animation */}
        <label className="flex items-center gap-1 md:gap-2 px-2 md:px-3 py-1.5 md:py-2 rounded-lg font-medium text-xs md:text-sm cursor-pointer transition-all duration-200 bg-white text-gray-700 border border-gray-200 hover:border-nokia-blue hover:text-nokia-blue shadow-sm hover:shadow-md">
          <input
            type="checkbox"
            checked={useRouteAnimatePresence}
            onChange={toggleRouteAnimatePresence}
            className="w-3 h-3 md:w-4 md:h-4 text-nokia-blue border-gray-300 rounded focus:ring-nokia-blue focus:ring-2 cursor-pointer"
            aria-label={t('toggleMainContext')}
          />
          <span className="hidden sm:inline">
            {useRouteAnimatePresence ? t('mainContextRouteAp') : t('mainContextSimple')}
          </span>
        </label>
        {/* Animation Environment Switch Button */}
        <button
          onClick={toggleAnimationMode}
          className={`flex items-center gap-1 md:gap-2 px-2 md:px-4 py-1.5 md:py-2 rounded-lg font-medium text-xs md:text-sm transition-all duration-200 ${
            useTransformersAnimation
              ? 'bg-nokia-blue text-white shadow-[0_4px_12px_rgba(0,69,245,0.3)] hover:shadow-[0_6px_16px_rgba(0,69,245,0.4)]'
              : 'bg-white text-gray-700 border border-gray-200 hover:border-nokia-blue hover:text-nokia-blue shadow-sm hover:shadow-md'
          }`}
          aria-label={useTransformersAnimation ? t('switchToSimpleAnimation') : t('switchToAdvancedAnimation')}
        >
          <span className="material-symbols-outlined text-base md:text-[18px]">
            {useTransformersAnimation ? 'animation' : 'auto_awesome'}
          </span>
          <span className="hidden sm:inline">
            {useTransformersAnimation ? t('advancedAnimation') : t('simpleAnimation')}
          </span>
        </button>
        <a className="hidden md:flex items-center gap-2 text-base text-gray-500 hover:text-nokia-blue transition-colors font-medium" href="#">
          <span className="material-symbols-outlined text-[20px]">help</span>
          {t('support')}
        </a>
        {/* Logout Button - only show when authenticated and not on auth pages */}
        {showLogout && (
          <button
            onClick={handleLogout}
            className="flex items-center gap-1 md:gap-2 px-3 md:px-4 py-1.5 md:py-2 rounded-lg font-medium text-xs md:text-sm transition-all duration-200 bg-red-50 text-red-600 border border-red-200 hover:bg-red-100 hover:border-red-300 dark:bg-red-900/30 dark:text-red-200 dark:border-red-700/70 dark:hover:bg-red-900/50 dark:hover:border-red-700 shadow-sm hover:shadow-md"
            aria-label={tCommon('logout')}
          >
            <span className="material-symbols-outlined text-base md:text-[18px]">logout</span>
            <span className="hidden sm:inline">{tCommon('logout')}</span>
          </button>
        )}
      </div>
    </nav>
  );
};

export default TopBar;
